<template>
  <v-wait :for="loaderId">
    <template slot="waiting">
      <half-circle-spinner />
      <div class="spinner-message my-3 text-center">Loading data...</div>
    </template>
    <b-row>
      <b-col cols="12">
        <div class="font_size_1250rem my-2 v2_font_weight_semibold">
          <p>Metropolitan Area</p>
        </div>
      </b-col>
    </b-row>
    <div class="area-container">
    <b-row>
      <b-col
        cols="12"
        md="6" sm="6"
        class="my-2 built-in-query v2_font_weight_semibold"
      >
        <b-form-group
          class=""
          label="Search for a metro area:"
        >
              <b-form-input
                v-model="areaSearch"
                placeholder="Search for an area…"
                class="mb-2"
              />
              <b-button variant="v3_teal_outline" @click="resetSearch" class="py-2">
                Reset
              </b-button>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6" sm="6" class="my-2 built-in-query">
        <b-form-group
          label="Metropolitan Area:"
          label-class="v2_font_weight_semibold"
        >
          <div class="v2_phairify-select-wrapper">
            <b-form-select
              class="phairify-border-grey area-select"
              :value="selectedAreaCode"
              :options="areaOptions"
              :state="validateState('selectedAreaCode')"
              @change="onAreaChange"
            >
              <template slot="first">
                <option :value="null" disabled>-- Select --</option>
              </template>
            </b-form-select>
          </div>
          <div
            v-show="$v.selectedAreaCode.$anyError"
            tabindex="-1"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            class="valid-feedback"
          >
              Select from {{ areaOptions.length }} metro areas
          </div>
        </b-form-group>
      </b-col>
      <template v-if="isDivisionVisible">
        <b-col cols="12" class="my-3">
          <b-form-group
            label="Division (optional):"
            label-class="v2_font_weight_semibold"
          >
            <div class="v2_phairify-select-wrapper">
              <b-form-select
                class="phairify-border-grey area-select"
                :value="selectedDivision"
                :options="divisionsOptions"
                :disabled="!selectedAreaCode"
                @change="onDivisionChange"
              >
                <template slot="first">
                  <option :value="null" disabled>-- Select --</option>
                </template>
              </b-form-select>
            </div>
          </b-form-group>
        </b-col>
      </template>
    </b-row>
    </div>
  </v-wait>
</template>

<script>
import { validationMixin } from 'vuelidate';
import validateStateMixin from '@/mixins/validateStateMixin';
import { required } from 'vuelidate/lib/validators';
import ExplanationBox from '@/components/ui-elements/ExplanationBox';
import { HalfCircleSpinner } from 'epic-spinners';
import Api from '@/providers/Api';
import { GEOGRAPHIC_PREFERENCE } from '@/helpers/jobQuestionsHelper';
import SubFormValidationMethodsMixin from '@/mixins/SubFormValidationMethodsMixin';

export default {
  name: 'AreaGeographyForm',
  mixins: [validationMixin, validateStateMixin, SubFormValidationMethodsMixin],
  props: {
    preferenceData: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  components: {
    ExplanationBox,
    HalfCircleSpinner,
  },
  data: function () {
    return {
      cbsaValues: [],
      areaSearch: '',
    };
  },
  validations: {
    selectedAreaCode: {
      required,
    },
    selectedDivision: {},
  },
  computed: {
    filteredCbsaValues: function () {
      if (!this.cbsaValues || this.areaSearch === '') return this.cbsaValues;
      return this.cbsaValues.filter(({ cbsa_name }) =>
        cbsa_name.toLowerCase().includes(this.areaSearch.toLowerCase())
      );
    },
    areaOptions: function () {
      return this.filteredCbsaValues.map(({ cbsa_code, cbsa_name }) => ({
        value: cbsa_code,
        text: cbsa_name,
      }));
    },
    isDivisionVisible: function () {
      return (
        this.selectedAreaObject && this.selectedAreaObject.cbsa_type === 'div'
      );
    },
    divisionsOptions: function () {
      if (
        !this.isDivisionVisible ||
        !this.selectedAreaObject ||
        !Array.isArray(this.selectedAreaObject.divs)
      ) {
        return [];
      }
      return this.selectedAreaObject.divs.map(({ div_name, div_code }) => ({
        value: div_code,
        text: div_name,
      }));
    },
    selectedAreaCode: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.cbsa_code
        : null;
    },
    selectedAreaObject: function () {
      return this.cbsaValues.find(
        ({ cbsa_code }) => cbsa_code === this.selectedAreaCode
      );
    },
    selectedDivision: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.div_code
        : null;
    },
    currentDisplayName: function () {
      return this.preferenceData && this.preferenceData.value
        ? this.preferenceData.value.display_name
        : null;
    },
    loaderId: function () {
      return `${this.$options.name}_loading`;
    },
  },
  methods: {
    getData: async function () {
      this.$wait.start(this.loaderId);
      try {
        this.cbsaValues = await Api.getCbsaValues();
      } catch (e) {
        this.$toasted.error('Unable to upload Area data', { duration: 3000 });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    resetSearch: function () {
      this.areaSearch = '';
    },
    onAreaChange: function (selected) {
      const { text: displayName = null } =
        this.areaOptions.find(({ value }) => selected === value) || {};
      this.$emit('set-preference', {
        type: GEOGRAPHIC_PREFERENCE.METROPOLITAN,
        value: {
          cbsa_code: selected,
          div_code: null,
          display_name: displayName,
        },
      });
      this.$v.selectedAreaCode.$touch();
    },
    onDivisionChange: function (selected) {
      const { text: displayName = this.currentDisplayName } =
        this.divisionsOptions.find(({ value }) => selected === value) || {};
      this.$emit('set-preference', {
        type: GEOGRAPHIC_PREFERENCE.METROPOLITAN,
        value: {
          cbsa_code: this.selectedAreaCode,
          div_code: selected,
          display_name: displayName,
        },
      });
      this.$v.selectedDivision.$touch();
    },
  },
  created: function () {
    this.getData();
  },
  watch: {
    selectedAreaCode: function () {
      this.onDivisionChange(null);
    },
    areaSearch: function () {
      this.onAreaChange(null);
    },
  },
};
</script>

<style lang="scss" scoped>
.valid-feedback {
  display: block;
  color: rgb(2, 22, 35);
  font-size: 90%;
}
.area-container {
  border: 1px solid grey;
  padding: 15px 25px;
}
.area-select {
  height: 40px;
  padding: 0 5px;
  &.is-invalid, &.is-invalid:focus {
    border-width: 2px;
    border-color: #d0de3a;
    background: none;
  }
}
@media (min-width:768px) and (max-width:1330px) {
  .built-in-query {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
  .area-container{
    padding: 15px 15px !important;
  }
}
</style>
